import { useState, useCallback, useMemo, ReactNode } from 'react';
import { Marker } from '@adamscybot/react-leaflet-component-marker';
import { useMap, useMapEvents } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import { COMPONENT_ICON_OPTS, PULSATION_STYLE } from '@features/components/LocationTracking/constants';
import { DeviceNodeTypeEnum } from '@/api/apis/composer';

const ALWAYS_SHOW_HOVER_CONTENT_MIN_ZOOM = 12;

type MapMarkerProps = {
  id: string;
  position: LatLngExpression;
  hoverContent?: ReactNode;
  showPulsation?: boolean;
  nodeType?: DeviceNodeTypeEnum;
};

const PulsatingDot = () => (
  <div className="relative w-12 h-12 rounded-full flex items-center justify-center" style={PULSATION_STYLE}>
    <span
      aria-hidden="true"
      className="absolute -inset-1 inline-flex rounded-full bg-orange-10 opacity-30 animate-ping"
    />
    <div className="w-3 h-3 rounded-full border-3 bg-white border-warning-40" />
  </div>
);

const DefaultDot = () => <div className="w-3 h-3 rounded-full border-3 bg-white border-warning-40" />;

const renderMarkerBody = (showPulsation: boolean): ReactNode => (showPulsation ? <PulsatingDot /> : <DefaultDot />);

export const MapMarker = ({ id, position, hoverContent, showPulsation = false, nodeType }: MapMarkerProps) => {
  const map = useMap();
  const [isHovered, setIsHovered] = useState(false);
  const [zoom, setZoom] = useState(map.getZoom());

  useMapEvents(
    useMemo(
      () => ({
        zoomend: () => setZoom(map.getZoom()),
      }),
      [map],
    ),
  );

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleFocus = useCallback(() => setIsHovered(true), []);
  const handleBlur = useCallback(() => setIsHovered(false), []);

  const showContent = zoom >= ALWAYS_SHOW_HOVER_CONTENT_MIN_ZOOM || isHovered;

  return (
    <Marker
      key={id}
      position={position}
      zIndexOffset={40}
      componentIconOpts={COMPONENT_ICON_OPTS}
      icon={
        <div
          className="relative flex flex-col justify-center items-center"
          role="img"
          aria-label="Map marker"
          data-node-type={nodeType}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {renderMarkerBody(showPulsation)}
          {showContent && hoverContent && <div className="absolute bottom-full mb-2 z-50">{hoverContent}</div>}
        </div>
      }
    />
  );
};
